// Fast create — one input: paste a URL or type an address/name.
// Shared by the dashboard inline form and the iOS-shortcut passthrough
// (#/add?url=…). Optimistic write to Firestore (instant card); URL-backed
// properties get a background scrape (Cloud Run) that upgrades title/image.
import { createProperty, findPropertyBySourceUrl, updateProperty } from './firestore.js';
import { refreshListing } from './listing.js';

const URLISH = /^https?:\/\//i;

/** Create a property from raw input. Returns { pid }, or { dup } when the
 * source URL is already on the user's list (caller should open that one).
 * `status` (one of STATUSES, already validated by the caller) creates the
 * property with that status — and on a dup, applies it to the existing
 * property instead (the extension's combo add: add + set state in one step,
 * even when the listing was already on the list). */
export async function createFromInput(user, raw, { status = null } = {}) {
  const sourceUrl = URLISH.test(raw) ? raw : null;
  const title = sourceUrl ? new URL(raw).hostname : raw;

  // Client-side dedup against my own properties before creating.
  if (sourceUrl) {
    const dup = await findPropertyBySourceUrl(user.uid, sourceUrl);
    if (dup) {
      if (status && dup.status !== status) {
        await updateProperty(dup.id, { status });
      }
      return { dup };
    }
  }

  const { pid } = await createProperty(user.uid, { title, sourceUrl, status });
  if (sourceUrl && !user.isAnonymous) {
    // Background upgrade — card is already on the dashboard; the snapshot
    // re-renders it when the scrape + geocode land. Failure is non-fatal.
    // Guests skip it: scrape/geocode are login-only (server 403).
    refreshListing(pid, sourceUrl, title).catch((e) => console.warn('listing refresh failed', e));
  }
  return { pid };
}
